/**
 * Whether a mapping can be imported with, and why not when it cannot.
 *
 * **Exactly one email column is required.** An item with no address cannot
 * become a contact, so a mapping without one would import nothing and report
 * success; two would mean a row with two answers to which address is theirs.
 *
 * The name is optional, because monday's item name is used when no column is
 * mapped to it — every item has one, and it is nearly always the person.
 */
export function mappingProblems(mapping) {
    const kinds = Object.values(mapping).map((target) => target.kind);
    const emails = kinds.filter((kind) => kind === 'email').length;
    const problems = [];
    if (emails === 0)
        problems.push('One column has to be the email address.');
    if (emails > 1)
        problems.push('Only one column can be the email address.');
    const keys = Object.values(mapping)
        .filter((target) => target.kind === 'field')
        .map((target) => target.key);
    if (new Set(keys).size !== keys.length) {
        problems.push('Two columns are mapped to the same field.');
    }
    const names = kinds.filter((kind) => kind === 'name').length;
    if (names > 1)
        problems.push('Only one column can be the name.');
    // Two columns answering "may we email this person" is two answers, and the
    // one that wins would be whichever the mapping happened to list last.
    if (kinds.filter((kind) => kind === 'subscription').length > 1) {
        problems.push('Only one column can say whether they are subscribed.');
    }
    return problems;
}
/**
 * The column mapped to the address, or nothing.
 *
 * Not exported: `toImportRow` below is the only caller, and a second way of
 * finding the address column would be a second answer to where addresses come
 * from.
 */
function emailColumnOf(mapping) {
    return Object.entries(mapping).find(([, target]) => target.kind === 'email')?.[0];
}
/**
 * One monday item as a row for the contact importer.
 *
 * **One definition, two callers.** A board import turns thousands of items into
 * rows; a webhook turns one. If those disagreed, a contact created by the
 * import and the same contact created by the sync would carry different fields
 * — and nobody would notice until a merge token rendered blank on one of them.
 *
 * The item's own name is the fallback for the contact's name, because every
 * monday item has one and it is nearly always the person: a board whose item
 * name *is* the person is the ordinary case rather than a special one.
 *
 * An empty address is returned as an empty string rather than refused here. Who
 * may be skipped and who must be reported is the caller's decision — the import
 * counts them, the webhook answers that there was nobody to add.
 */
export function toImportRow(item, mapping) {
    const emailColumn = emailColumnOf(mapping);
    const custom = {};
    let name = '';
    let status;
    for (const [columnId, target] of Object.entries(mapping)) {
        const value = item.values[columnId];
        if (value === undefined || value === '')
            continue;
        if (target.kind === 'name')
            name = value;
        if (target.kind === 'field')
            custom[target.key] = value;
        if (target.kind === 'subscription')
            status = subscriptionFromText(value);
    }
    return {
        email: (emailColumn === undefined ? '' : (item.values[emailColumn] ?? '')).trim(),
        name: name === '' ? item.name : name,
        custom,
        status,
    };
}
/**
 * What kind of column a monday column should become here.
 *
 * **So that a board can be imported by somebody who has declared nothing.** The
 * mapper could only offer fields that already existed, which on a fresh
 * organisation is none — so a board with a Subscription, a Company name and a
 * Phone offered three choices: skip it, call it the address, or call it the
 * name. The answer is to let a column be made from the mapping screen, and this
 * is what decides what kind it is made as.
 *
 * A guess, and deliberately a conservative one: getting it wrong costs a column
 * of the wrong shape, which is renamed and retyped from the contacts board in a
 * moment. Anything not listed becomes `text`, because text holds whatever
 * monday rendered and nothing is lost — a `mirror` or a `formula` arrives as
 * the string somebody was already reading on the board.
 *
 * `long_text` becomes text rather than a kind of its own: the distinction is
 * how monday draws an editor, and a contact field has no long form.
 */
export function fieldTypeForMondayColumn(mondayType) {
    switch (mondayType) {
        case 'email':
            return 'email';
        case 'phone':
            return 'phone';
        case 'link':
            return 'url';
        case 'date':
        case 'timeline':
            return 'date';
        case 'numbers':
        case 'rating':
            return 'number';
        case 'checkbox':
            return 'boolean';
        case 'status':
            return 'status';
        case 'dropdown':
            return 'dropdown';
        default:
            return 'text';
    }
}
/**
 * What a board's own words for a subscription mean here.
 *
 * A "Subscription" column on monday is free text with a colour behind it: one
 * board says Subscribed/Unsubscribed, another says Opted in/Opted out, another
 * Active/Churned. There is no vocabulary to look up, so this reads the ones
 * people actually write and **treats anything it does not recognise as
 * unsubscribed**.
 *
 * That asymmetry is the whole point. Guessing "subscribed" wrongly sends
 * marketing to somebody who asked not to receive it, which is not undone by
 * noticing later; guessing "unsubscribed" wrongly means somebody who would
 * have been happy to hear from you does not, which is one edit to fix. When the
 * cost of the two mistakes is that different, the safe one is the default.
 *
 * **An empty cell is not a value.** It returns `undefined` rather than
 * unsubscribed, because a blank says nothing about this person — on plenty of
 * boards most rows are blank — and `ImportRow.status` already means "absent is
 * subscribed". A blank column would otherwise silently suppress a whole board.
 */
export function subscriptionFromText(value) {
    const said = value.trim().toLowerCase();
    if (said === '')
        return undefined;
    if (SUBSCRIBED.has(said))
        return 'subscribed';
    if (CLEANED.has(said))
        return 'cleaned';
    return 'unsubscribed';
}
/** Said of somebody who may be sent to. Anything not here is not assumed to be. */
const SUBSCRIBED = new Set([
    'subscribed',
    'subscribe',
    'subscriber',
    'opted in',
    'opted-in',
    'opt in',
    'opt-in',
    'active',
    'yes',
    'y',
    'true',
    '1',
    'marketing contact',
    'marketing',
]);
/** An address that cannot receive rather than a person who declined. */
const CLEANED = new Set([
    'cleaned',
    'bounced',
    'hard bounce',
    'hard bounced',
    'invalid',
    'undeliverable',
]);
//# sourceMappingURL=monday.js.map